/**
 * Skelet aplikace: levé menu šesti sekcí, hlavička s globálním vyhledáváním
 * a indikátorem čerstvosti, pravý vysouvací chat panel (spec 08 §3, §4, §5).
 */
import { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/hooks/useTheme';
import { useAiConfig, useLogout } from '@/api/queries';
import { EtlFreshnessIndicator, EtlStaleBanner } from './EtlFreshnessIndicator';
import { GlobalSearch } from './GlobalSearch';
import { ChatPanel } from '@/chat/ChatPanel';

const SECTIONS = [
  { to: '/', label: 'Přehled', exact: true, icon: GridIcon },
  { to: '/compare', label: 'Porovnání', icon: ScaleIcon },
  { to: '/devices', label: 'Zařízení', icon: DeviceIcon },
  { to: '/clicks', label: 'Mapa kliků', icon: TargetIcon },
  { to: '/experiments', label: 'Experimenty', icon: FlaskIcon },
  { to: '/guard', label: 'Guard', icon: ShieldIcon },
  { to: '/knowledge', label: 'Znalosti', icon: BookIcon },
  { to: '/ai', label: 'AI', icon: SparkIcon },
];

export function AppLayout() {
  const [chatOpen, setChatOpen] = useState(false);
  const { user, isAdmin } = useAuth();
  const { mode, setTheme } = useTheme();
  const logout = useLogout();
  const location = useLocation();
  const { data: aiConfig } = useAiConfig();

  // Chat lze globálně vypnout (07 FR-3) — pak panel z UI úplně zmizí.
  const chatEnabled = (aiConfig?.chat_enabled ?? true) && (aiConfig?.ai_available ?? true);

  return (
    <div className={`app-shell${chatOpen && chatEnabled ? ' app-shell--chat-open' : ''}`}>
      <aside className="app-nav">
        <div className="app-nav__brand">
          <span className="app-nav__logo" aria-hidden="true">◧</span>
          <span className="app-nav__name">StickyIQ</span>
        </div>

        <nav className="app-nav__sections" aria-label="Hlavní navigace">
          {SECTIONS.map(({ to, label, exact, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={exact}
              className={({ isActive }) => `app-nav__link${isActive ? ' is-active' : ''}`}
            >
              <Icon />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="app-nav__footer">
          {isAdmin ? (
            <NavLink
              to="/admin"
              className={({ isActive }) => `app-nav__link app-nav__link--sm${isActive ? ' is-active' : ''}`}
            >
              <CogIcon />
              <span>Správa</span>
            </NavLink>
          ) : null}

          <button
            type="button"
            className="app-nav__link app-nav__link--sm"
            onClick={() => setTheme(mode === 'dark' ? 'light' : 'dark')}
            title={mode === 'dark' ? 'Přepnout na světlý režim' : 'Přepnout na tmavý režim'}
          >
            {mode === 'dark' ? <SunIcon /> : <MoonIcon />}
            <span>{mode === 'dark' ? 'Světlý' : 'Tmavý'}</span>
          </button>

          <div className="app-nav__user">
            <div className="app-nav__user-name" title={user?.email}>
              {user?.name || user?.email}
            </div>
            <div className="app-nav__user-role">{user?.role}</div>
            <button
              type="button"
              className="app-nav__logout"
              onClick={() => logout.mutate()}
            >
              Odhlásit
            </button>
          </div>
        </div>
      </aside>

      <div className="app-main">
        <header className="app-header">
          <GlobalSearch />
          <div className="app-header__right">
            <EtlFreshnessIndicator />
            {chatEnabled ? (
              <button
                type="button"
                className={`chat-toggle${chatOpen ? ' is-active' : ''}`}
                onClick={() => setChatOpen((v) => !v)}
                aria-expanded={chatOpen}
              >
                <ChatIcon />
                <span>Chat</span>
              </button>
            ) : null}
          </div>
        </header>

        <EtlStaleBanner />

        <main className="app-content">
          <Outlet />
        </main>
      </div>

      {chatEnabled ? (
        <ChatPanel
          open={chatOpen}
          onClose={() => setChatOpen(false)}
          pageContext={{ page: location.pathname }}
        />
      ) : null}
    </div>
  );
}

/* --- ikony (inline, ať nemusíme tahat ikonovou knihovnu) --- */

function GridIcon() {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
      <rect x="2" y="2" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.3" />
      <rect x="9" y="2" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.3" />
      <rect x="2" y="9" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.3" />
      <rect x="9" y="9" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  );
}
function FlaskIcon() {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
      <path d="M6.5 2v4L3 12.5A1 1 0 0 0 3.9 14h8.2a1 1 0 0 0 .9-1.5L9.5 6V2" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M5.5 2h5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}
function ShieldIcon() {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
      <path d="M8 2l5 2v4c0 3-2 5-5 6-3-1-5-3-5-6V4l5-2Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  );
}
function ScaleIcon() {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
      <path d="M8 2v12M3 5h10M4.5 5 2.5 9.5h4L4.5 5ZM11.5 5 9.5 9.5h4L11.5 5Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    </svg>
  );
}
function DeviceIcon() {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
      <rect x="1.5" y="3" width="9" height="7" rx="1" stroke="currentColor" strokeWidth="1.3" />
      <path d="M4 13h5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <rect x="11" y="6" width="3.5" height="7.5" rx="1" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  );
}
function TargetIcon() {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
      <rect x="1.5" y="1.5" width="13" height="13" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
      <path d="M11 2v3.5h3.5" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <circle cx="7" cy="9" r="2" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  );
}
function BookIcon() {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
      <path d="M3 3h4a2 2 0 0 1 2 2v8a1.5 1.5 0 0 0-1.5-1.5H3V3Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M13 3H9a2 2 0 0 0-2 2v8a1.5 1.5 0 0 1 1.5-1.5H13V3Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  );
}
function SparkIcon() {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
      <path d="M8 2l1.4 3.6L13 7l-3.6 1.4L8 12l-1.4-3.6L3 7l3.6-1.4L8 2Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    </svg>
  );
}
function CogIcon() {
  return (
    <svg viewBox="0 0 16 16" width="15" height="15" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="2.2" stroke="currentColor" strokeWidth="1.3" />
      <path d="M8 1.8v1.6M8 12.6v1.6M14.2 8h-1.6M3.4 8H1.8M12.4 3.6l-1.1 1.1M4.7 11.3l-1.1 1.1M12.4 12.4l-1.1-1.1M4.7 4.7 3.6 3.6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}
function SunIcon() {
  return (
    <svg viewBox="0 0 16 16" width="15" height="15" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="3" stroke="currentColor" strokeWidth="1.3" />
      <path d="M8 1v1.5M8 13.5V15M15 8h-1.5M2.5 8H1M12.9 3.1l-1 1M4.1 11.9l-1 1M12.9 12.9l-1-1M4.1 4.1l-1-1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}
function MoonIcon() {
  return (
    <svg viewBox="0 0 16 16" width="15" height="15" fill="none" aria-hidden="true">
      <path d="M13 9.5A5.5 5.5 0 0 1 6.5 3a5.5 5.5 0 1 0 6.5 6.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  );
}
function ChatIcon() {
  return (
    <svg viewBox="0 0 16 16" width="15" height="15" fill="none" aria-hidden="true">
      <path d="M14 8.5c0 2.5-2.7 4.5-6 4.5-.7 0-1.4-.1-2-.3L3 14l.6-2.3C2.6 10.9 2 9.8 2 8.5 2 6 4.7 4 8 4s6 2 6 4.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  );
}
